import { useState, useEffect, useRef, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  ArrowRight,
  LogOut,
  Menu,
  X,
  Home,
  Layers,
  Beaker,
  ListChecks,
  Bell,
  Lightbulb,
  Microscope,
  Info,
  Crown,
  BookOpen,
} from "lucide-react";
import { Button } from "./ui/button";
import { useAuth } from "../lib/auth";
import { useAuthStatus } from "../lib/useAuthStatus";
import { LogoSymbol, LogoWordmark, LogoTagline } from "./Logo";
import ProfileSwitcher from "./ProfileSwitcher";

function Wordmark() {
  // Two viewport-targeted lockups — mobile below sm, full size at sm+.
  // The tagline is now always visible because it IS the product description.
  return (
    <>
      {/* Mobile (< 640px) — compact lockup, tagline included so users
          immediately see what the product does. */}
      <span className="flex sm:hidden items-center" style={{ gap: 10 }}>
        <LogoSymbol size={46} color="#111111" />
        <span className="flex flex-col min-w-0" style={{ gap: 3 }}>
          <LogoWordmark size={22} />
          <LogoTagline size={10.5} />
        </span>
      </span>
      {/* Desktop (≥ 640px) — full-size brand. */}
      <span className="hidden sm:flex items-center" style={{ gap: 20 }}>
        <LogoSymbol size={76} color="#111111" />
        <span className="flex flex-col" style={{ gap: 6 }}>
          <LogoWordmark size={40} />
          <LogoTagline size={17} />
        </span>
      </span>
    </>
  );
}

// Slim center nav (desktop) — H1 simplification: just the 4 most important
// destinations. Secondary links (Categories, Browse, FAQ, About, etc.) live in
// the mobile drawer and the site footer.
const CENTER_LINKS = [
  { label: "Home", to: "/", testid: "nav-home" },
  { label: "How it works", href: "/#how", testid: "nav-how" },
  { label: "Routine Checker", to: "/routine", testid: "nav-routine" },
  { label: "Pro", to: "/pro", testid: "nav-pro" },
  { label: "Science", href: "/#trust", testid: "nav-science" },
];

// Mobile drawer — kept short & scannable: a primary "Menu" group (mirrors the
// desktop nav + Founding) and a compact "More" group for secondary pages.
const DRAWER_GROUPS = [
  {
    title: "Menu",
    items: [
      { label: "Home", to: "/", icon: Home, testid: "drawer-home" },
      { label: "How it works", href: "/#how", icon: Lightbulb, testid: "drawer-how" },
      { label: "Routine Checker", to: "/routine", icon: ListChecks, testid: "drawer-routine" },
      { label: "Pro", to: "/pro", icon: Crown, testid: "drawer-pro" },
      { label: "Science", href: "/#trust", icon: Microscope, testid: "drawer-science" },
      { label: "Founding Access", href: "/#founding", icon: Crown, testid: "drawer-founding", accent: "#a87a32" },
    ],
  },
  {
    title: "More",
    items: [
      { label: "Categories", to: "/categories", icon: Layers, testid: "drawer-categories" },
      { label: "Browse Ingredients", to: "/browse", icon: Beaker, testid: "drawer-browse" },
      { label: "Watchlist", to: "/watchlist", icon: Bell, testid: "drawer-watchlist" },
      { label: "FAQ", to: "/faq", icon: Info, testid: "drawer-faq" },
    ],
  },
];

// Desktop center nav — a segmented capsule with a single highlight pill that
// slides to the active route and glides under whatever link is hovered.
// Motion lives on `left`/`width` (measured from the DOM) so it tracks any
// label length and the forest-green pill never jumps.
const FOREST = "#1F3D2E";

function CenterNav() {
  const location = useLocation();
  const navRef = useRef(null);
  const itemsRef = useRef([]);
  const [hovered, setHovered] = useState(null);
  const [pill, setPill] = useState({ left: 0, width: 0, show: false });

  const activeIndex = CENTER_LINKS.findIndex((l) =>
    l.to ? (l.to === "/" ? location.pathname === "/" : location.pathname.startsWith(l.to)) : false,
  );
  const target = hovered != null ? hovered : activeIndex >= 0 ? activeIndex : null;

  const measure = useCallback(() => {
    const nav = navRef.current;
    const el = target != null ? itemsRef.current[target] : null;
    if (!nav || !el) {
      setPill((p) => ({ ...p, show: false }));
      return;
    }
    const nr = nav.getBoundingClientRect();
    const er = el.getBoundingClientRect();
    setPill({ left: er.left - nr.left, width: er.width, show: true });
  }, [target]);

  useLayoutEffect(() => { measure(); }, [measure, location.pathname]);
  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  return (
    <nav
      ref={navRef}
      data-testid="desktop-nav"
      onMouseLeave={() => setHovered(null)}
      className="relative hidden xl:flex items-center gap-0.5 rounded-full border border-surface-line bg-white/55 px-1.5 py-1 no-select"
      style={{ boxShadow: "inset 0 1px 0 rgba(255,255,255,0.6), 0 1px 2px rgba(20,17,12,0.04)" }}
    >
      <span
        aria-hidden
        className="absolute top-1 bottom-1 rounded-full transition-all duration-300 ease-out"
        style={{
          left: pill.left,
          width: pill.width,
          opacity: pill.show ? 1 : 0,
          background: FOREST,
          boxShadow: "0 4px 12px -5px rgba(31,61,46,0.6)",
        }}
      />
      {CENTER_LINKS.map((l, i) => {
        const under = target === i;
        const cls = `relative z-[1] px-3.5 py-1.5 rounded-full text-[13px] font-medium tracking-wide whitespace-nowrap transition-colors duration-200 ${
          under ? "text-cream-50" : "text-ink-mute hover:text-ink"
        }`;
        const common = {
          "data-testid": l.testid,
          ref: (el) => (itemsRef.current[i] = el),
          onMouseEnter: () => setHovered(i),
          className: cls,
        };
        return l.to ? (
          <NavLink key={l.testid} to={l.to} {...common}>
            {l.label}
          </NavLink>
        ) : (
          <a key={l.testid} href={l.href} {...common}>
            {l.label}
          </a>
        );
      })}
    </nav>
  );
}

export default function Header({ onSignIn }) {
  const { user, signOut } = useAuth();
  const { enabled: authEnabled } = useAuthStatus();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const closeDrawer = () => setDrawerOpen(false);

  // Lock body scroll when drawer is open + ESC closes.
  // iOS Safari pitfall: setting only `body.style.overflow = 'hidden'` can
  // cause the page to snap to the top on close. We instead pin the body
  // via `position: fixed; top: -scrollY` and restore the exact scroll
  // position when the drawer closes — eliminates the "auto-scroll" feel
  // some users reported on mobile.
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const scrollY = window.scrollY;
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    const html = document.documentElement;
    const body = document.body;
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    document.addEventListener("keydown", onKey);
    return () => {
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";
      body.style.width = "";
      body.style.overflow = "";
      html.style.overscrollBehavior = "";
      // Restore the scroll position without a smooth animation so the user
      // lands exactly where they were before opening the drawer.
      window.scrollTo({ top: scrollY, behavior: "instant" });
      document.removeEventListener("keydown", onKey);
    };
  }, [drawerOpen]);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-surface-line/60"
      // Inline style guarantees the opaque backdrop renders — Tailwind's
      // `bg-cream-50/98` opacity-modifier was resolving to `rgba(0,0,0,0)`
      // in production (likely because our custom `cream-50` colour is
      // defined as a hex string rather than channel-based, so the `/98`
      // alpha modifier silently drops). Inline rgba + backdrop-blur gives
      // us a premium glass-morphism header that fully covers content
      // beneath when the user scrolls.
      style={{
        background: "rgba(254, 250, 243, 0.92)",
        backdropFilter: "saturate(140%) blur(14px)",
        WebkitBackdropFilter: "saturate(140%) blur(14px)",
        boxShadow: "0 1px 0 rgba(20,17,12,0.05), 0 6px 18px -14px rgba(20,17,12,0.18)",
      }}
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 md:px-10 h-20 sm:h-[96px] flex items-center justify-between gap-4">
        {/* Left: Logo — nudged left at md+ so its box aligns with the page
            content container (max-w-screen-xl px-4 → 96px @ 1440). `relative`
            + `left` shifts it visually without moving the nav/right cluster. */}
        <Link to="/" data-testid="header-logo-link" className="group shrink-0 md:relative md:-left-6">
          <Wordmark />
        </Link>

        {/* Center: segmented pill nav with a sliding highlight (desktop only) */}
        <CenterNav />

        {/* Right: user pill / Sign in / CTA / Hamburger */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Subtle secondary founding link (desktop only) — keeps the offer
              accessible without the loud champagne pill that crowded the bar. */}
          {!user && (
            <a
              href="/#founding"
              data-testid="nav-founding"
              className="hidden xl:inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-semibold transition-all hover:scale-[1.03] active:scale-[0.98]"
              style={{
                color: "#a87a32",
                borderColor: "rgba(168,122,50,0.32)",
                background: "linear-gradient(135deg,#fdf7e9 0%,#f6ecd1 100%)",
              }}
            >
              <Crown className="w-3.5 h-3.5" strokeWidth={2.3} />
              Founding access
            </a>
          )}

          {user ? (
            <>
              <span className="hidden xl:block"><ProfileSwitcher variant="desktop" /></span>
              <span
                data-testid="header-quota-pill"
                className="hidden 2xl:inline-flex items-center gap-2 text-[12px] px-3 py-1.5 rounded-full bg-cream-100 border border-surface-line text-ink-soft"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-safe-solid" />
                <span className="font-medium text-ink">
                  {user.is_pro ? "Pro" : "Free"}
                </span>
              </span>
              <Button
                data-testid="header-cta-check"
                onClick={() => navigate("/check")}
                className="rounded-full h-8 sm:h-10 px-3 sm:px-5 bg-ink hover:bg-ink-soft text-cream-50 text-[11px] sm:text-[13px] font-medium transition-all hover:scale-[1.02] active:scale-[0.98] whitespace-nowrap"
              >
                <span className="hidden sm:inline">Check compatibility</span>
                <span className="sm:hidden">Check</span>
                <ArrowRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 ml-1 sm:ml-1.5" />
              </Button>
            </>
          ) : (
            <>
              {authEnabled && (
                <button
                  data-testid="header-sign-in"
                  onClick={onSignIn}
                  className="hidden xl:inline-flex whitespace-nowrap text-[13px] text-ink-soft hover:text-ink transition-colors px-2"
                >
                  Sign in
                </button>
              )}
              <Button
                data-testid="header-cta-start"
                onClick={() => navigate("/check")}
                className="rounded-full h-8 sm:h-10 px-3 sm:px-5 bg-ink hover:bg-ink-soft text-cream-50 text-[11px] sm:text-[13px] font-medium transition-all hover:scale-[1.02] active:scale-[0.98] whitespace-nowrap"
              >
                <span className="hidden sm:inline">Check compatibility</span>
                <span className="sm:hidden">Check</span>
                <ArrowRight className="w-3 h-3 sm:w-3.5 sm:h-3.5 ml-1 sm:ml-1.5" />
              </Button>
            </>
          )}

          {/* Hamburger — mobile + tablet only (desktop has the inline nav). */}
          <button
            type="button"
            data-testid="menu-toggle"
            onClick={() => setDrawerOpen(true)}
            className="xl:hidden inline-flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 rounded-full text-ink hover:bg-cream-100 transition-colors"
            aria-label="Open menu"
            aria-expanded={drawerOpen}
          >
            <Menu className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>
      </div>

      {/* Right slide-in drawer — portaled to body so the <header>'s
          backdrop-filter doesn't trap fixed positioning. */}
      {typeof document !== "undefined" && createPortal(
        <>
          <div
            data-testid="drawer-backdrop"
            aria-hidden
            onClick={closeDrawer}
            className={`fixed inset-0 z-[100] bg-black/45 transition-opacity duration-300 ${
              drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          />
          <aside
            data-testid="nav-drawer"
            role="dialog"
            aria-label="Site menu"
            aria-hidden={!drawerOpen}
            className={`fixed top-0 right-0 bottom-0 z-[101] w-[88vw] max-w-[420px] bg-cream-50 border-l border-surface-line shadow-2xl transition-transform duration-300 ease-out flex flex-col ${
              drawerOpen ? "translate-x-0 pointer-events-auto" : "translate-x-full pointer-events-none"
            }`}
          >
        <div className="flex items-center justify-between px-5 h-20 sm:h-[96px] border-b border-surface-line shrink-0">
          <span className="text-[11px] uppercase tracking-[0.18em] text-ink-mute font-semibold">
            Menu
          </span>
          <button
            type="button"
            data-testid="drawer-close"
            onClick={closeDrawer}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full hover:bg-cream-100 text-ink"
            aria-label="Close menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto overscroll-contain px-3 py-3.5 drawer-scroll">
          {/* Primary CTA — clearly visible at the top of the mobile menu. */}
          <button
            type="button"
            data-testid="drawer-cta-check"
            onClick={() => { closeDrawer(); navigate("/check"); }}
            className="mb-4 w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-ink hover:bg-ink-soft text-cream-50 text-[15px] font-semibold transition-all active:scale-[0.99]"
          >
            Check compatibility
            <ArrowRight className="w-4 h-4" />
          </button>

          {DRAWER_GROUPS.map((group) => (
            <section key={group.title} className="mb-4">
              <h3 className="text-[10.5px] uppercase tracking-[0.2em] text-ink-mute font-semibold px-3 mb-1.5">
                {group.title}
              </h3>
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const Icon = item.icon;
                  const isAccent = !!item.accent;
                  const inner = (
                    <>
                      <span
                        className={
                          isAccent
                            ? "inline-flex items-center justify-center w-9 h-9 rounded-xl shrink-0 transition-colors"
                            : "inline-flex items-center justify-center w-9 h-9 rounded-xl bg-white border border-surface-line text-ink-soft shrink-0 transition-colors"
                        }
                        style={
                          isAccent
                            ? {
                                background: "linear-gradient(135deg,#fdf7e9 0%,#f6ecd1 100%)",
                                color: item.accent,
                                border: "1px solid rgba(168,122,50,0.28)",
                              }
                            : undefined
                        }
                      >
                        <Icon
                          className="w-4 h-4"
                          strokeWidth={isAccent ? 2.4 : 2.2}
                        />
                      </span>
                      <span className="flex-1 text-[15px] font-medium leading-tight text-ink">
                        {item.label}
                        {isAccent && (
                          <span
                            className="ml-2 inline-flex items-center align-middle rounded-full px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-[0.16em]"
                            style={{
                              color: "#a87a32",
                              background: "rgba(168,122,50,0.10)",
                              border: "1px solid rgba(168,122,50,0.22)",
                            }}
                          >
                            Early
                          </span>
                        )}
                      </span>
                      <ArrowRight
                        className="w-4 h-4 shrink-0"
                        style={{ color: isAccent ? "#a87a32" : undefined }}
                        strokeWidth={isAccent ? 2.4 : 2}
                      />
                    </>
                  );
                  const baseCls =
                    "flex items-center gap-3 px-3 py-2 rounded-xl transition-colors min-h-[48px]";
                  const accentCls = isAccent
                    ? "bg-gradient-to-r from-[rgba(253,247,233,0.55)] to-transparent border border-[rgba(168,122,50,0.18)] hover:from-[rgba(253,247,233,0.85)]"
                    : "hover:bg-cream-100";
                  const cls = `${baseCls} ${accentCls}`;
                  return (
                    <li key={item.testid}>
                      {item.to ? (
                        <NavLink
                          to={item.to}
                          data-testid={item.testid}
                          onClick={closeDrawer}
                          className={cls}
                        >
                          {inner}
                        </NavLink>
                      ) : (
                        <a
                          href={item.href}
                          data-testid={item.testid}
                          onClick={closeDrawer}
                          className={cls}
                        >
                          {inner}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}

          {/* Account section */}
          <section className="mt-2 pt-4 border-t border-surface-line space-y-1">
            {user ? (
              <>
                <div className="flex items-center gap-2 px-3 py-2 text-[12.5px] text-ink-soft">
                  <span className="w-1.5 h-1.5 rounded-full bg-safe-solid" />
                  <span className="font-medium text-ink">
                    {user.is_pro ? "Pro" : "Free"}
                  </span>
                </div>
                <div className="px-1 pb-1"><ProfileSwitcher variant="drawer" /></div>
                <button
                  data-testid="drawer-account"
                  onClick={() => { closeDrawer(); navigate("/account"); }}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-cream-100 text-[15px] text-ink-soft min-h-[48px]"
                >
                  Your account
                  <ArrowRight className="w-4 h-4 text-ink-light" />
                </button>
                <button
                  data-testid="drawer-my-routines"
                  onClick={() => { closeDrawer(); navigate("/routines"); }}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-cream-100 text-[15px] text-ink-soft min-h-[48px]"
                >
                  My routines
                  <ArrowRight className="w-4 h-4 text-ink-light" />
                </button>
                {user.is_admin && (
                  <button
                    data-testid="drawer-admin"
                    onClick={() => { closeDrawer(); navigate("/admin"); }}
                    className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-cream-100 text-[15px] text-peach-500 font-semibold min-h-[48px]"
                  >
                    Admin panel
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}
                <button
                  data-testid="drawer-sign-out"
                  onClick={() => { signOut(); closeDrawer(); }}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-cream-100 text-[15px] text-ink-light min-h-[48px]"
                >
                  Sign out
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              authEnabled && (
                <button
                  data-testid="drawer-sign-in"
                  onClick={() => { closeDrawer(); onSignIn?.(); }}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl hover:bg-cream-100 text-[15px] text-ink font-medium min-h-[48px]"
                >
                  Sign in
                  <ArrowRight className="w-4 h-4 text-ink-light" />
                </button>
              )
            )}
          </section>
        </nav>

        {/* Branded footer line — emotional polish at the bottom of the drawer. */}
        <div
          data-testid="drawer-footer"
          className="shrink-0 px-5 pt-3 pb-5 border-t border-surface-line/70 bg-cream-50"
        >
          <p className="text-[11.5px] text-ink-mute leading-relaxed">
            Prescription-aware skincare safety. Built in the US.
          </p>
          <p className="text-[10.5px] text-ink-light mt-0.5 tracking-[0.04em]">
            blendsafe.com — calm, careful, considered.
          </p>
        </div>
      </aside>
        </>,
        document.body,
      )}
    </header>
  );
}
